import { Component, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";

import { OptionsAdminService } from "../../services/options-admin.service";
import { CurrentUserService } from "../../services/current-user.service";
import { BrowserWidthService } from "../../services/browser-width.service";
import { HttpResponseService } from '../../services/httpresponse-service';
import { LoadingSpinnerService } from "../../services/loading-spinner.service";
import { ToasterService, ToasterConfig } from 'angular2-toaster';

import { Option } from "../../model/option";
import { AdminOptionCategory } from '../../model/admin-option-category';
import { Category } from '../../model/category';
import { AdminOption } from '../../model/admin-option';


@Component({
    selector: 'options-change-category-aside',
    template: require('./options-aside-category-change.component.html'),
	styles: [require('./options-aside-category-change.component.scss')]
})
export class OptionsChangeCategoryAsideComponent implements OnDestroy {
    private BULKCATEGORYCHANGE = 'Bulk Home Category Change';
    private BULKCHANGE = 'Bulk Change';

    plantId: number = null;
    homeCatNumber: number = 0;
    customerCatNumber: number = 0;

    userId: string;
    homeCatButton: string = 'Home Categories';
    customerCatButton: string = 'Customer Categories';

    options: Option[];
    subscription: Subscription = new Subscription(); 
    homeCategories: Category[];
    customerCategories: AdminOptionCategory[];
    selectedProductLineCode: string = null;

    // added below vars for selected option bulk changes
    maxQuantity: number = 0;
    maxQuantityChanged: boolean = false;
    visible: boolean = false;
    visibilityChanged: boolean = false;
    selectedOptionsCategory: number;
    selectedOptionsCategoryChanged: boolean = false;
    selectedOptionsCategoryError: boolean = false;
    pageTitle: string = this.BULKCATEGORYCHANGE;
    bulkchangeOptionList: Option[] = null;
    showBulkChangeOption: boolean = false;

    config: ToasterConfig = new ToasterConfig({
        positionClass: this.browserWidthService.getDisplayMode().width.innerWidth < 769 ? 'toast-bottom-full-width' : 'toast-bottom-right'
    });

    constructor(
        public optionsAdminService: OptionsAdminService,
        private currentUserService: CurrentUserService,
        public browserWidthService: BrowserWidthService,
        private httpResponseService: HttpResponseService,
        private spinner: LoadingSpinnerService,
        private toasterService: ToasterService,
    ) {
        const subPlantId = this.optionsAdminService.plantId$.subscribe(plantid => {
            this.plantId = plantid;
            this.loadHomeCategories(this.plantId);
        });
        this.subscription.add(subPlantId);

        const currentUserSub = this.currentUserService.currentUser$.subscribe(x => {
            if (x !== undefined && x !== null) {
                if (x.cmhUserId !== this.userId) {
                    this.userId = x.cmhUserId;
                }
            }
        });
        this.subscription.add(currentUserSub);

        const bulkchangeOptionSub = this.optionsAdminService.bulkchangeOptions$.subscribe(bch => {
            this.showBulkChangeOption = false;
            this.bulkchangeOptionList = bch;
            this.showBulkChangeOption = bch === null || bch === undefined ? false : bch.length > 0;
            this.pageTitle = (bch === null || bch === undefined) ? this.BULKCATEGORYCHANGE : this.BULKCHANGE;
        });
        this.subscription.add(bulkchangeOptionSub);

        const selectedProductLineCodeSub = this.optionsAdminService.selectedProductLineCode$.subscribe(plc => { this.selectedProductLineCode = plc;  });
        this.subscription.add(selectedProductLineCodeSub);
    }

    loadHomeCategories(plantId: number) {
        if (plantId > 0) {
            this.options = this.optionsAdminService.getOptionsByPlant(plantId);
            this.homeCategories = this.optionsAdminService.getHomeCategoriesCache;
            this.sortHomeCategories();
            this.customerCategories = this.optionsAdminService.getCustomerCategoriesCache;
            this.sortCustomerCategories();
            this.optionsAdminService.allCustomerCategories$.next(this.customerCategories);
        }
    }

    sortHomeCategories() {
        if (this.homeCategories !== undefined && this.homeCategories !== null && this.homeCategories.length > 1) {
            this.homeCategories.sort((a, b) => {
                if (a.CategoryDescription < b.CategoryDescription) { return -1; }
                if (a.CategoryDescription > b.CategoryDescription) { return -1; }
                return 0;
            });
        }
    }

    /// Sorts the Customer Categories
    sortCustomerCategories() {
        if (this.customerCategories !== undefined && this.customerCategories !== null && this.customerCategories.length > 1) {
            this.customerCategories.sort((a, b) => {
                if (a.WebCategoryDesc < b.WebCategoryDesc) { return -1; }
                if (a.WebCategoryDesc > b.WebCategoryDesc) { return -1; }
                return 0;
            });
        }
    }

    closeAside() {
        this.homeCatButton = 'Home Categories';
        this.customerCatButton = 'Customer Categories';
        this.homeCatNumber = 0;
        this.customerCatNumber = 0;
        this._resetBulkOptionChangeFields();
        this.optionsAdminService.showBulkCategoryChangeAside$.next(false);
        this.optionsAdminService.uncheckBulkChangeSelectedOptions$.next(true);
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    /// Selects the Category in the Modal Dropdown
    bulkCategoryChange(homeCat: boolean, categoryDesc: string) {
        if (homeCat) {
            this.homeCatButton = this.homeCategories.find(x => x.CategoryDescription === categoryDesc).CategoryDescription;
            this.homeCatNumber = this.homeCategories.find(x => x.CategoryDescription === categoryDesc).CategoryId;
        } else {
            this.customerCatButton = this.customerCategories.find(x => x.WebCategoryDesc === categoryDesc).WebCategoryDesc;
            this.customerCatNumber = this.customerCategories.find(x => x.WebCategoryDesc === categoryDesc).WebCategoryId;
        }
    }

    /// Changes the Home Categories to Customer Categories
    saveBulkChangeCategories() {
        if (!this.showBulkChangeOption) {
            if (!this.disabledBulkChangeSave) {
                this.spinner.showSpinner();
                this.optionsAdminService.bulkCategoryChange(this.plantId, this.homeCatNumber, this.customerCatNumber)
                    .subscribe(
                    (data => {
                        if (data) {
                            this.toasterService.pop('success', 'Success!', 'Bulk Home Category Changes have been saved');
                        }
                    }),
                    (error => {
                        this.spinner.hideSpinner();
                        this.httpResponseService.httpErrorResponse$.next(error);
                    }),
                    () => {
                        this.optionsAdminService.reloadOptionsAdmin$.next(true);
                        this.homeCatButton = 'Home Categories';
                        this.customerCatButton = 'Customer Categories';
                        this.homeCatNumber = 0;
                        this.customerCatNumber = 0;
                        this._resetBulkOptionChangeFields();
                        this.optionsAdminService.uncheckBulkChangeSelectedOptions$.next(true);
                        this.spinner.hideSpinner();
                    });
            }
        } else {
            if ((this.selectedOptionsCategory === 0 || this.selectedOptionsCategory === undefined || this.selectedOptionsCategory === null) && this.selectedOptionsCategoryChanged) {
                this.selectedOptionsCategoryError = true;
            } else {
                // save selected admin options
                this.spinner.showSpinner();
                let adminOptions = this._buildAdminOptionForSave(this.selectedProductLineCode);

                if (adminOptions !== null && adminOptions !== undefined) {
                    if (adminOptions.length > 0) {
                        // save the specific option admin option
                        this.optionsAdminService.insUpdAdminOption(adminOptions, this.userId).subscribe(
                            (data => {
                                if (data) {
                                    this.toasterService.pop('success', 'Success!', 'Bulk Option Changes have been saved');
                                }
                            }),
                            (error => { this.spinner.hideSpinner(); this.httpResponseService.httpErrorResponse$.next(error); }),
                            (() => {
                                this.optionsAdminService.reloadOptionsAdmin$.next(true);
                                this._resetBulkOptionChangeFields();
                                this.spinner.hideSpinner();
                            })
                        );
                    }
                }            }

        }
    }

    /// Determines if the Bulk Home Categories can be saved
    get disabledBulkChangeSave(): boolean {
        if (!this.showBulkChangeOption) {
            return ((this.homeCatButton !== '' && this.homeCatButton !== 'Home Categories') && (this.customerCatButton !== '' && this.customerCatButton !== 'Customer Categories')
                && (this.homeCatNumber !== null && this.homeCatNumber !== 0) && (this.customerCatNumber !== null && this.customerCatNumber !== 0))
                ? false : true;
        } else {
            return (this.selectedOptionsCategoryChanged || this.maxQuantityChanged || this.visibilityChanged) ? false : true;
        }
    }

    // builds and saves admin options for selected options
    getWebCategory(webCategoryId: number): AdminOptionCategory {
        if (this.customerCategories !== undefined && this.customerCategories !== null && this.customerCategories.length > 0) {
            let webCat = this.customerCategories.find(x => x.WebCategoryId === Number(webCategoryId));
            let other = this.customerCategories.find(x => x.WebCategoryDesc.toUpperCase() === 'OTHER');

            if (webCat !== undefined && webCat !== null) {
                return webCat;
            }
            if (other !== undefined && other !== null) {
                return other;
            }
        }
        return new AdminOptionCategory();
    }

    onVisibilityChange(isVisible: boolean) {
        this.visibilityChanged = true;
        this.visible = isVisible;
    }

    onMaxQuantityChange() {
        this.maxQuantityChanged = true;
    }

    onOptionCategoryChange() {
        this.selectedOptionsCategoryChanged = true;
        this.selectedOptionsCategoryError = false;
        if (this.selectedOptionsCategory === 0) {
            this.selectedOptionsCategoryError = true;
        }
    }

    private _buildAdminOptionForSave(productLineCode: string): AdminOption[] {
        let adminOptions = new Array<AdminOption>();
        if (this.bulkchangeOptionList !== null && this.bulkchangeOptionList !== undefined) {
            this.bulkchangeOptionList.forEach(option => {
                //adminOptions = new Array<AdminOption>();

                let webCat = this.getWebCategory(Number(this.selectedOptionsCategory));
                let productLineId = 0;
                if (option.ProductLines.length > 0) {
                    productLineId = option.ProductLines.filter(pl => pl.ProductLineCode === productLineCode)[0].ProductLineId;
                }
                if (option.AdminOption !== undefined && option.AdminOption !== null) {
                    option.AdminOption.WebCategoryId = webCat.WebCategoryId;
                    option.AdminOption.PlantId = option.PlantId;
                    option.AdminOption.ProductLineId = productLineId;
                    option.AdminOption.OptionId = option.OptionId;
                    option.AdminOption.OptionNumber = option.OptionNumber;
                    option.AdminOption.AdminOptionId = option.AdminOption.AdminOptionId !== null ? option.AdminOption.AdminOptionId : 0;
                    option.AdminOption.CustomerDesc = option.AdminOption.CustomerDesc;
                    option.AdminOption.CustomerCat = this.selectedOptionsCategoryChanged ? webCat.WebCategoryDesc : option.AdminOption.CustomerCat;
                    option.AdminOption.MaxQuantity = this.maxQuantityChanged ? this.maxQuantity : option.AdminOption.MaxQuantity;
                    option.AdminOption.Visible = (this.visibilityChanged) ? this.visible : option.AdminOption.Visible;
                    adminOptions.push(option.AdminOption);
                }

                if (option.SubOptions !== undefined && option.SubOptions !== null && option.SubOptions.length > 0) {
                    option.SubOptions.forEach((so) => {
                        if (so.AdminOption !== undefined && so.AdminOption !== null) {
                            so.AdminOption.WebCategoryId = webCat.WebCategoryId;
                            so.AdminOption.PlantId = option.PlantId;
                            so.AdminOption.ProductLineId = Number(productLineId);
                            so.AdminOption.OptionId = option.OptionId;
                            so.AdminOption.OptionNumber = option.OptionNumber;
                            so.AdminOption.SubOptionId = so.SubOptionId;
                            so.AdminOption.AdminOptionId = option.AdminOption.AdminOptionId !== null ? option.AdminOption.AdminOptionId : 0;
                            so.AdminOption.CustomerDesc = so.AdminOption.CustomerDesc;
                            so.AdminOption.CustomerCat = this.selectedOptionsCategoryChanged ? webCat.WebCategoryDesc : option.AdminOption.CustomerCat;
                            adminOptions.push(so.AdminOption);
                        }
                    });
                }
            });
        }

        return adminOptions;
    }

    private _resetBulkOptionChangeFields() {
        this.selectedOptionsCategory = -1;
        this.selectedOptionsCategoryChanged = false;
        this.selectedOptionsCategoryError = false;
        this.maxQuantity = 0;
        this.maxQuantityChanged = false;
        this.visible = false;
        this.visibilityChanged = false;
    }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/reusable-components/options-admin-category-change-aside/options-aside-category-change.component.ts